import { lazy, Suspense } from "react";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import TrustSection from "./components/TrustSection";
import HomeSchemas from "./components/HomeSchemas";
import { useSeoMeta } from "@/hooks/useSeoMeta";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://ltfsourcing.com";

// Below-the-fold sections — code-split into separate chunks
const AboutSection = lazy(() => import("./components/AboutSection"));
const WhyChooseUs = lazy(() => import("./components/WhyChooseUs"));
const ServicesSection = lazy(() => import("./components/ServicesSection"));
const HowItWorks = lazy(() => import("./components/HowItWorks"));
const OverstockSection = lazy(() => import("./components/OverstockSection"));
const TestimonialsSection = lazy(() => import("./components/TestimonialsSection"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const ContactSection = lazy(() => import("./components/ContactSection"));
const Footer = lazy(() => import("./components/Footer"));

// Thin placeholder that keeps layout stable while a section loads
const SectionSkeleton = () => <div className="w-full py-24 bg-white" />;

export default function Home() {
  useSeoMeta({
    title: "LTF Sourcing | Product Sourcing, Wholesale Supply & Inventory Solutions",
    description:
      "LTF Sourcing is a global procurement partner offering product sourcing, wholesale supply across 40+ categories, and overstock inventory solutions. Verified suppliers, end-to-end logistics, serving businesses across 8+ countries.",
    canonical: `${SITE_URL}/`,
    ogTitle: "LTF Sourcing | Product Sourcing, Wholesale Supply & Inventory Solutions",
    ogDescription:
      "Global procurement partner — verified product sourcing, wholesale supply across 40+ categories, and overstock inventory solutions. Serving 75+ businesses across 8+ countries.",
    ogType: "website",
    keywords:
      "product sourcing company, wholesale supply partner, overstock inventory solutions, supplier verification, procurement outsourcing, bulk buying, inventory liquidation",
  });

  return (
    <main className="min-h-screen w-full">
      <HomeSchemas />
      <Navbar />
      {/* Above-the-fold — eager */}
      <HeroSection />
      <TrustSection />
      {/* Below-the-fold — lazy */}
      <Suspense fallback={<SectionSkeleton />}>
        <AboutSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <WhyChooseUs />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <ServicesSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <HowItWorks />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <OverstockSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <TestimonialsSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <FAQSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton />}>
        <ContactSection />
      </Suspense>
      <Suspense fallback={<div className="w-full py-16 bg-[#f5f5f5]" />}>
        <Footer />
      </Suspense>
    </main>
  );
}
